/**
 * Live Score View - Public view of live match
 */
import { useState, useEffect } from 'react';
import { axiosInstance } from '../../config/axios';
import { adjustmentsApi } from '../rulebook/api';

export default function LiveScoreView({ eventId, fixtureId, liveScore, innings, onRefresh }) {
  const [events, setEvents] = useState([]);
  const [recentBalls, setRecentBalls] = useState([]);
  const [adjustments, setAdjustments] = useState([]);

  // Runs or points the umpire gave or took away; refreshed with every live update
  useEffect(() => {
    if (!eventId || !fixtureId) return;
    adjustmentsApi.list(eventId, fixtureId).then(setAdjustments).catch(() => {});
  }, [eventId, fixtureId, liveScore]);

  useEffect(() => {
    if (liveScore?.recent_balls) {
      setRecentBalls(liveScore.recent_balls);
    }
    if (liveScore?.recent_events) {
      setEvents(liveScore.recent_events);
    }
  }, [liveScore]);

  const formatOvers = (overs) => {
    if (!overs) return '0.0';
    return overs.toFixed(1);
  };

  const getBallDisplay = (ball) => {
    if (ball.is_wicket) return { text: 'W', class: 'bg-red-500 text-white' };
    if (ball.is_boundary_six) return { text: '6', class: 'bg-purple-500 text-white' };
    if (ball.is_boundary_four) return { text: '4', class: 'bg-blue-500 text-white' };
    if (ball.extras_type === 'wide') return { text: 'WD', class: 'bg-amber-400 text-white' };
    if (ball.extras_type === 'no_ball') return { text: 'NB', class: 'bg-amber-400 text-white' };
    if (ball.runs_scored === 0) return { text: '0', class: 'bg-gray-200 text-gray-600' };
    return { text: String(ball.runs_scored), class: 'bg-green-100 text-green-700' };
  };

  const FEED_STYLES = {
    wicket: { icon: 'ph-warning', badge: 'bg-red-100 text-red-600' },
    boundary: { icon: 'ph-confetti', badge: 'bg-blue-100 text-blue-600' },
    milestone: { icon: 'ph-trophy', badge: 'bg-amber-100 text-amber-600' },
    over_summary: { icon: 'ph-list-bullets', badge: 'bg-gray-100 text-gray-600' },
    innings_summary: { icon: 'ph-article', badge: 'bg-[#3AAFA9]/10 text-[#3AAFA9]' },
    match_summary: { icon: 'ph-article', badge: 'bg-[#3AAFA9]/10 text-[#3AAFA9]' },
  };
  const feedStyle = (type) => FEED_STYLES[type] || { icon: 'ph-info', badge: 'bg-gray-100 text-gray-600' };

  const currentInnings = innings.find(i => i.status === 'in_progress') || innings[innings.length - 1];

  if (!currentInnings) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-broadcast text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">Match Not Started</h3>
        <p className="text-sm text-gray-500">
          Live scores will appear here when the match begins.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-score-view">
      {/* Main Scorecard */}
      <div className="bg-gradient-to-r from-[#2B3A67] to-[#1a2744] rounded-xl p-6 text-white">
        {/* Live Badge */}
        {currentInnings.status === 'in_progress' && (
          <div className="flex items-center gap-2 mb-4">
            <span className="flex items-center gap-1.5 px-2 py-1 bg-red-500 rounded-full text-xs font-bold animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full"></span>
              LIVE
            </span>
            <span className="text-white/60 text-xs">{currentInnings.kind === 'super_over' ? 'Super over' : `Innings ${currentInnings.innings_number}`}</span>
          </div>
        )}

        {/* Score */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">{currentInnings.batting_team_name}</h2>
            <p className="text-white/60 text-sm">vs {currentInnings.bowling_team_name}</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-extrabold">
              {currentInnings.total_runs}
              <span className="text-2xl text-white/70">/{currentInnings.total_wickets}</span>
            </p>
            <p className="text-white/60">
              {formatOvers(currentInnings.total_overs)} overs
            </p>
          </div>
        </div>

        {/* Run Rate */}
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-white/60">CRR:</span>
            <span className="ml-1 font-bold">{currentInnings.run_rate || 0}</span>
          </div>
          {currentInnings.target_runs && (
            <>
              <div>
                <span className="text-white/60">Target:</span>
                <span className="ml-1 font-bold">{currentInnings.target_runs}</span>
              </div>
              <div>
                <span className="text-white/60">RRR:</span>
                <span className="ml-1 font-bold">{currentInnings.required_rate || '-'}</span>
              </div>
            </>
          )}
        </div>

        {/* Adjustments (rulebook penalties and bonuses) */}
        {adjustments.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="live-adjustments">
            {adjustments.map((a) => (
              <span key={a.id} className={`text-xs font-semibold px-2 py-1 rounded-lg ${a.amount < 0 ? 'bg-[#E54562]/25' : 'bg-[#3AAFA9]/25'}`}>
                {a.amount > 0 ? '+' : ''}{a.amount} {a.kind === 'runs' ? (Math.abs(a.amount) === 1 ? 'run' : 'runs') : (Math.abs(a.amount) === 1 ? 'pt' : 'pts')} {a.team_name}: {a.reason}
              </span>
            ))}
          </div>
        )}

        {/* Match Situation */}
        {liveScore?.match_situation && (
          <div className="mt-4 p-3 bg-white/10 rounded-lg">
            <p className="text-sm">{liveScore.match_situation}</p>
          </div>
        )}
      </div>

      {/* Current Partnership */}
      {liveScore?.current_partnership && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Current Partnership</h4>
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="font-bold text-[#2B3A67]">{liveScore.current_partnership.batsman1_name}</p>
              <p className="text-lg font-bold">
                {liveScore.current_partnership.batsman1_runs}
                <span className="text-gray-400 text-sm"> ({liveScore.current_partnership.batsman1_balls})</span>
              </p>
            </div>
            <div className="px-4 text-center">
              <p className="text-2xl font-bold text-[#3AAFA9]">
                {liveScore.current_partnership.total_runs}
              </p>
              <p className="text-xs text-gray-500">
                {liveScore.current_partnership.total_balls} balls
              </p>
            </div>
            <div className="text-center flex-1">
              <p className="font-bold text-[#2B3A67]">{liveScore.current_partnership.batsman2_name}</p>
              <p className="text-lg font-bold">
                {liveScore.current_partnership.batsman2_runs}
                <span className="text-gray-400 text-sm"> ({liveScore.current_partnership.batsman2_balls})</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Balls */}
      {recentBalls.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">This Over</h4>
          <div className="flex gap-2 flex-wrap">
            {recentBalls.slice(-12).map((ball, idx) => {
              const display = getBallDisplay(ball);
              return (
                <div
                  key={ball.id || idx}
                  className={`relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${display.class} ${
                    ball.is_power_ball ? 'ring-2 ring-offset-1 ring-[#F4D341]' : ball.is_free_hit ? 'ring-2 ring-offset-1 ring-[#E54562]' : ''}`}
                  title={ball.commentary}
                >
                  {display.text}
                  {!!ball.bonus_runs && (
                    <span className={`absolute -top-1.5 -right-1.5 text-[9px] leading-none px-1 py-0.5 rounded-full font-extrabold text-white ${
                      ball.bonus_runs > 0 ? 'bg-[#3AAFA9]' : 'bg-[#E54562]'}`}>
                      {ball.bonus_runs > 0 ? '+' : ''}{ball.bonus_runs}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ball-by-ball commentary (latest first) */}
      {recentBalls.some(b => b.commentary) && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid="live-commentary">
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Commentary</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {[...recentBalls].reverse().slice(0, 8).filter(b => b.commentary).map((ball, idx) => (
              <div key={ball.id || idx} className="flex gap-3 text-sm">
                <span className="font-mono text-xs text-gray-400 w-10 flex-shrink-0 pt-0.5">
                  {ball.over_number}.{ball.ball_number || '•'}
                </span>
                <p className="text-gray-700 flex-1 min-w-0">{ball.commentary}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Events */}
      {events.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Match Feed</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {events.map((event, idx) => (
              <div 
                key={event.id || idx}
                className={`flex gap-3 p-2 rounded-lg ${
                  event.priority === 2 ? 'bg-red-50' :
                  event.priority === 1 ? 'bg-blue-50' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${feedStyle(event.event_type).badge}`}>
                  <i className={`ph-bold ${feedStyle(event.event_type).icon}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-800">{event.title}</p>
                  {event.description && (
                    <p className="text-xs text-gray-500">{event.description}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Innings */}
      {innings.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Innings Summary</h4>
          <div className="space-y-2">
            {innings.map((inn, idx) => (
              <div 
                key={inn.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  inn.status === 'in_progress' ? 'bg-[#3AAFA9]/10 border border-[#3AAFA9]/30' : 'bg-gray-50'
                }`}
              >
                <div>
                  <p className="font-medium text-[#2B3A67]">{inn.batting_team_name}</p>
                  <p className="text-xs text-gray-500">{inn.kind === 'super_over' ? 'Super over' : `Innings ${inn.innings_number}`}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">
                    {inn.total_runs}/{inn.total_wickets}
                  </p>
                  <p className="text-xs text-gray-500">{formatOvers(inn.total_overs)} overs</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
